import { RecordChip } from 'components/record-chip/RecordChip';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import type { GlobalSearchEntityType } from 'types/globalSearch';
import { resolveRecordFromPath } from 'utils/globalSearchRoutes';
import { isAiChatRecordPath } from '../components/message/aiChatLinkResolver';

/** `[`INV-2026-1187`](/invoice/1187)` — the one markdown shape a seeded prompt names a record with. */
const RECORD_LINK_PATTERN = /\[`([^`\]]+)`\]\((\/[^\s)]+)\)/;

export interface SeededRecordPrompt {
    before: string;
    name: string;
    record: { entityType: GlobalSearchEntityType; id: number };
    after: string;
}

/**
 * The record a seeded prompt names, or null when it names none — only the shape the peek card
 * composes, and only when its path really is a record's. Everything else is the plain field's.
 */
export function readSeededRecordPrompt(prompt: string | null): SeededRecordPrompt | null {
    if (prompt === null) {
        return null;
    }
    const match = RECORD_LINK_PATTERN.exec(prompt);
    const record = match && isAiChatRecordPath(match[2]) ? resolveRecordFromPath(match[2]) : null;
    if (!match || !record) {
        return null;
    }
    return {
        before: prompt.slice(0, match.index),
        name: match[1],
        record,
        after: prompt.slice(match.index + match[0].length),
    };
}

export interface SeededPromptPreviewProps {
    prompt: SeededRecordPrompt;
    onEdit: () => void;
}

/**
 * The seeded question as it will read once sent, drawn in the composer's place until it is touched.
 *
 * A `textarea` cannot draw a chip inside its own text, so the record is drawn as the chip it
 * becomes in the conversation. Pressing anywhere else hands the markdown back to the field, still
 * amendable and still unsent.
 */
export function SeededPromptPreview({ prompt, onEdit }: SeededPromptPreviewProps) {
    const intl = useIntl();
    const ref = useRef<HTMLDivElement>(null);

    // The field this stands in for is not mounted, so without taking the focus the panel opens with
    // nothing focused and the composer is unreachable by keyboard.
    useEffect(() => {
        ref.current?.focus();
    }, []);

    return (
        <div
            ref={ref}
            // Focusable and activated by Enter or Space: a pointer-only hand-over would put the
            // composer out of a keyboard's reach. Not `role="button"` — a button's contents are
            // presentational, and the chip has to stay a link.
            role="textbox"
            aria-readonly
            aria-label={intl.formatMessage({ id: 'ai-chat.floating.fab-label' })}
            tabIndex={0}
            onClick={onEdit}
            onKeyDown={(event) => {
                // Enter on the chip has to open the record, not be swallowed as a request to edit.
                if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) {
                    return;
                }
                event.preventDefault();
                onEdit();
            }}
            className="text-foreground text-body1 max-h-32 min-h-6 flex-1 cursor-text overflow-y-auto pe-2 leading-[1.85] whitespace-pre-wrap outline-none"
        >
            {prompt.before}
            <RecordChip
                entityType={prompt.record.entityType}
                id={prompt.record.id}
                label={prompt.name}
                target="_blank"
                rel="noopener noreferrer"
                // The chip is the one thing in here that is not an invitation to start editing.
                onClick={(event) => event.stopPropagation()}
                className="text-meta h-6 align-middle"
            />
            {prompt.after}
        </div>
    );
}
